/**
 * The people with accounts on this server.
 *
 * Two rules shape this page. **No password field ever appears for an account
 * that already exists** — an administrator who can silently set somebody's
 * password can read their messages as them, and the recovery path for a
 * forgotten password is a reset the person completes themselves. And **the
 * owner's row is inert**: the server refuses to demote, disable or delete the
 * owner, so offering the buttons here would only produce an error the operator
 * could not act on.
 */

import { useEffect, useMemo, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import { useAuth } from '@/api/auth'
import {
  useAdminUpdateUser,
  useAdminUsers,
  useCreateInvitation,
  useDeleteUser,
  useInvitations,
  useRevokeInvitation,
  useResetUserMfa,
  useSetUserStatus,
} from '@/api/queries'
import type {
  CreateInvitationRequest,
  Invitation,
  InvitationRole,
  User,
  UserStatus,
} from '@/api/types'
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Modal,
  Select,
  Spinner,
  TextField,
  useToast,
} from '@/components/ui'
import { formatRelative } from '@/lib/format'

import {
  AdminPage,
  Card,
  ErrorNotice,
  Notice,
  describeError,
  readField,
  readText,
  useCopy,
} from './AdminLayout'
import styles from './admin.module.css'

type StatusFilter = 'all' | UserStatus

const STATUS_FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'invited', label: 'Invited' },
]

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Active',
  disabled: 'Disabled',
  invited: 'Invited',
}

function statusTone(status: UserStatus): 'success' | 'warning' | 'neutral' {
  if (status === 'active') return 'success'
  if (status === 'disabled') return 'warning'
  return 'neutral'
}

function roleLabel(user: User): string {
  if (user.is_owner) return 'Owner'
  if (user.is_admin) return 'Admin'
  return 'Member'
}

function invitationRoleLabel(role: InvitationRole): string {
  if (role === 'admin') return 'Admin'
  if (role === 'guest') return 'Guest'
  return 'Member'
}

/** Owner first, then by the name a reader is actually scanning for. */
function byStanding(a: User, b: User): number {
  if (a.is_owner !== b.is_owner) return a.is_owner ? -1 : 1
  return a.display_name.localeCompare(b.display_name)
}

type UserListEntry =
  | { kind: 'user'; user: User }
  | { kind: 'invitation'; invitation: Invitation }

function isWaitingForAcceptance(invitation: Invitation, now: number): boolean {
  return (
    invitation.accepted_at === null &&
    invitation.revoked_at === null &&
    Date.parse(invitation.expires_at) > now
  )
}

function byListEntry(a: UserListEntry, b: UserListEntry): number {
  if (a.kind === 'user' && b.kind === 'user') return byStanding(a.user, b.user)
  if (a.kind === 'user') return -1
  if (b.kind === 'user') return 1
  return Date.parse(b.invitation.created_at) - Date.parse(a.invitation.created_at)
}

type Confirmation =
  | { kind: 'disable'; user: User }
  | { kind: 'delete'; user: User }
  | { kind: 'reset_mfa'; user: User }

export function UsersPage(): ReactElement {
  const auth = useAuth()
  const toast = useToast()

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [adding, setAdding] = useState(false)
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const [revokingInvitation, setRevokingInvitation] = useState<Invitation | null>(null)

  // Typing should not fire a request per keystroke, and the server's own
  // matching is what a search across hundreds of accounts needs.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const users = useAdminUsers(query)
  const invitations = useInvitations()
  const updateUser = useAdminUpdateUser()
  const setStatusOf = useSetUserStatus()
  const deleteUser = useDeleteUser()
  const revokeInvitation = useRevokeInvitation()
  const resetMfa = useResetUserMfa()

  const visible = useMemo(() => {
    const now = Date.now()
    const normalizedQuery = query.toLocaleLowerCase()
    const entries: UserListEntry[] = [
      ...(users.data ?? []).map((user) => ({ kind: 'user' as const, user })),
      ...(invitations.data ?? [])
        .filter((invitation) => isWaitingForAcceptance(invitation, now))
        .filter(
          (invitation): invitation is Invitation & { email: string } =>
            invitation.kind === 'email' && invitation.email !== null,
        )
        .filter((invitation) => invitation.email.toLocaleLowerCase().includes(normalizedQuery))
        .map((invitation) => ({ kind: 'invitation' as const, invitation })),
    ]

    const matching =
      status === 'all'
        ? entries
        : entries.filter((entry) =>
            entry.kind === 'user' ? entry.user.status === status : status === 'invited',
          )

    return matching.sort(byListEntry)
  }, [invitations.data, query, status, users.data])

  const selfId = auth.user?.id ?? null

  const changeRole = (user: User, admin: boolean): void => {
    updateUser.mutate(
      { userId: user.id, changes: { is_admin: admin } },
      {
        onSuccess: () => {
          toast.success(
            admin
              ? `${user.display_name} is now an administrator.`
              : `${user.display_name} is no longer an administrator.`,
          )
        },
        onError: (error: unknown) => {
          toast.error(describeError(error, 'That role could not be changed.'))
        },
      },
    )
  }

  const changeStatus = (user: User, next: UserStatus): void => {
    setStatusOf.mutate(
      { userId: user.id, status: next },
      {
        onSuccess: () => {
          setConfirmation(null)
          toast.success(
            next === 'active'
              ? `${user.display_name} can sign in again.`
              : `${user.display_name} has been signed out and cannot sign in.`,
          )
        },
        onError: (error: unknown) => {
          toast.error(describeError(error, 'That account could not be changed.'))
        },
      },
    )
  }

  const remove = (user: User): void => {
    deleteUser.mutate(user.id, {
      onSuccess: () => {
        setConfirmation(null)
        toast.success(`${user.display_name}'s account has been deleted.`)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That account could not be deleted.'))
      },
    })
  }

  const clearMfa = (user: User): void => {
    resetMfa.mutate(user.id, {
      onSuccess: () => {
        setConfirmation(null)
        toast.success(`${user.display_name} can set up two-factor authentication again.`)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'Two-factor authentication could not be reset.'))
      },
    })
  }

  const revokePendingInvitation = (): void => {
    if (revokingInvitation === null) return

    revokeInvitation.mutate(revokingInvitation.id, {
      onSuccess: () => {
        toast.success(`The invitation to ${revokingInvitation.email} has been revoked.`)
        setRevokingInvitation(null)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That invitation could not be revoked.'))
      },
    })
  }

  return (
    <AdminPage
      title="Users"
      lead="Everybody with an account on this server, plus people who are still deciding whether to join. Webhook bots are not listed here; they belong to the channel that created them."
      actions={
        <Button variant="primary" onClick={() => setAdding(true)}>
          Add someone
        </Button>
      }
    >
      <Card>
        <div className={styles.filters}>
          <TextField
            className={styles.grow}
            label="Search"
            type="search"
            value={search}
            placeholder="Name, username or invited email"
            onChange={(event) => setSearch(event.target.value)}
            autoComplete="off"
          />
          <Select
            label="Status"
            value={status}
            onChange={(event) => {
              const value = event.target.value
              setStatus(
                value === 'active' || value === 'disabled' || value === 'invited' ? value : 'all',
              )
            }}
            options={STATUS_FILTERS.map((filter) => ({
              value: filter.value,
              label: filter.label,
            }))}
          />
        </div>
      </Card>

      <Card>
        {users.isError || invitations.isError ? (
          <ErrorNotice
            error={users.error ?? invitations.error}
            title="The user list could not be loaded"
          >
            <Button
              onClick={() => {
                void users.refetch()
                void invitations.refetch()
              }}
            >
              Try again
            </Button>
          </ErrorNotice>
        ) : users.isPending || invitations.isPending ? (
          <Spinner size="lg" label="Loading users" />
        ) : visible.length === 0 ? (
          <EmptyState
            title="Nobody matches"
            description={
              query === '' && status === 'all'
                ? 'This server has no accounts or waiting invitations yet.'
                : 'Try a different search, or widen the status filter.'
            }
          />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Last seen</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((entry) => {
                  if (entry.kind === 'invitation') {
                    const { invitation } = entry

                    return (
                      <tr key={`invitation-${invitation.id}`}>
                        <th scope="row">
                          <span className={styles.identity}>
                            <Avatar
                              name={invitation.email ?? 'Invitation'}
                              id={invitation.id}
                              size="sm"
                              decorative
                            />
                            <span>
                              <span className={styles.identityName}>{invitation.email}</span>
                              <span className={styles.hint}>
                                Awaiting account setup · expires {formatRelative(invitation.expires_at)}
                              </span>
                            </span>
                          </span>
                        </th>

                        <td>{invitationRoleLabel(invitation.role)}</td>

                        <td>
                          <Badge tone="neutral">Invited</Badge>
                        </td>

                        <td className={styles.nowrap}>
                          <span className={styles.muted}>Awaiting acceptance</span>
                        </td>

                        <td>
                          <div className={styles.cellActions}>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => setRevokingInvitation(invitation)}
                            >
                              Revoke
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  }

                  const { user } = entry
                  const isSelf = user.id === selfId
                  const protectedRow = user.is_owner || !user.is_local

                  return (
                    <tr key={user.id}>
                      <th scope="row">
                        <span className={styles.identity}>
                          <Avatar
                            name={user.display_name}
                            id={user.id}
                            src={user.avatar_url}
                            size="sm"
                            decorative
                          />
                          <span>
                            <span className={styles.identityName}>{user.display_name}</span>
                            <span className={styles.hint}>
                              {user.qualified_id}
                              {user.mfa_enabled === true ? ' · 2FA' : ''}
                            </span>
                          </span>
                        </span>
                      </th>

                      <td>
                        {protectedRow ? (
                          <span className={styles.small}>
                            {roleLabel(user)}
                            {user.is_owner ? (
                              <span className={styles.hint}>
                                Changed by transferring ownership
                              </span>
                            ) : (
                              <span className={styles.hint}>Account on {user.server_id}</span>
                            )}
                          </span>
                        ) : (
                          <Select
                            label={`Role for ${user.display_name}`}
                            labelHidden
                            value={user.is_admin ? 'admin' : 'member'}
                            onChange={(event) => changeRole(user, event.target.value === 'admin')}
                            options={[
                              { value: 'member', label: 'Member' },
                              { value: 'admin', label: 'Admin' },
                            ]}
                            disabled={updateUser.isPending}
                          />
                        )}
                      </td>

                      <td>
                        <Badge tone={statusTone(user.status)}>{STATUS_LABEL[user.status]}</Badge>
                      </td>

                      <td className={styles.nowrap}>
                        {user.last_seen_at === null ? (
                          <span className={styles.muted}>Never</span>
                        ) : (
                          <span title={user.last_seen_at}>{formatRelative(user.last_seen_at)}</span>
                        )}
                      </td>

                      <td>
                        <div className={styles.cellActions}>
                          {user.mfa_enabled === true ? (
                            <Button
                              size="sm"
                              onClick={() => setConfirmation({ kind: 'reset_mfa', user })}
                              disabled={!user.is_local}
                            >
                              Reset 2FA
                            </Button>
                          ) : null}
                          {user.status === 'disabled' ? (
                            <Button
                              size="sm"
                              onClick={() => changeStatus(user, 'active')}
                              disabled={protectedRow || setStatusOf.isPending}
                            >
                              Enable
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => setConfirmation({ kind: 'disable', user })}
                              disabled={protectedRow || isSelf}
                            >
                              Disable
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setConfirmation({ kind: 'delete', user })}
                            disabled={protectedRow || isSelf}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={confirmation?.kind === 'disable'}
        onClose={() => setConfirmation(null)}
        title="Disable this account?"
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirmation(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={setStatusOf.isPending}
              onClick={() => {
                if (confirmation?.kind === 'disable') changeStatus(confirmation.user, 'disabled')
              }}
            >
              Disable account
            </Button>
          </>
        }
      >
        {confirmation?.kind === 'disable' ? (
          <p>
            <strong>{confirmation.user.display_name}</strong> is signed out everywhere and cannot
            sign in again until the account is enabled. Their messages and channel memberships are
            left alone.
          </p>
        ) : null}
      </Modal>

      <Modal
        open={confirmation?.kind === 'delete'}
        onClose={() => setConfirmation(null)}
        title="Delete this account?"
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirmation(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={deleteUser.isPending}
              onClick={() => {
                if (confirmation?.kind === 'delete') remove(confirmation.user)
              }}
            >
              Delete account
            </Button>
          </>
        }
      >
        {confirmation?.kind === 'delete' ? (
          <div className={styles.stack}>
            <p>
              <strong>{confirmation.user.display_name}</strong> ({confirmation.user.username}) loses
              their account and every session on it. This cannot be undone.
            </p>
            <Notice tone="warning">
              Disabling the account keeps the record and stops the access. Delete only when the
              record itself should go.
            </Notice>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={confirmation?.kind === 'reset_mfa'}
        onClose={() => setConfirmation(null)}
        title="Reset two-factor authentication?"
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirmation(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={resetMfa.isPending}
              onClick={() => {
                if (confirmation?.kind === 'reset_mfa') clearMfa(confirmation.user)
              }}
            >
              Reset two-factor
            </Button>
          </>
        }
      >
        {confirmation?.kind === 'reset_mfa' ? (
          <p>
            <strong>{confirmation.user.display_name}</strong> will be able to sign in with their
            password alone, and must set up an authenticator again if this server requires it. Use
            this when they have lost both the phone and the recovery codes.
          </p>
        ) : null}
      </Modal>

      <Modal
        open={revokingInvitation !== null}
        onClose={() => setRevokingInvitation(null)}
        title="Revoke this invitation?"
        size="sm"
        footer={
          <>
            <Button onClick={() => setRevokingInvitation(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={revokeInvitation.isPending}
              onClick={revokePendingInvitation}
            >
              Revoke invitation
            </Button>
          </>
        }
      >
        {revokingInvitation === null ? null : (
          <p>
            The link sent to <strong>{revokingInvitation.email}</strong> stops working immediately.
            They will not appear in this list again unless you send a new invitation.
          </p>
        )}
      </Modal>

      <AddPersonDialog open={adding} onClose={() => setAdding(false)} />
    </AdminPage>
  )
}

// ── Adding somebody ─────────────────────────────────────────────────────────

interface AddPersonDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * An administrator assigns only the invitation's address and server role. The
 * link recipient chooses their own username, display name and password when
 * they redeem it, so an administrator never needs to handle a colleague's
 * password or decide how they appear in chat.
 */
function AddPersonDialog({ open, onClose }: AddPersonDialogProps): ReactElement {
  const create = useCreateInvitation()
  const toast = useToast()
  const copy = useCopy()

  const [email, setEmail] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)

  const reset = (): void => {
    setEmail('')
    setIsAdmin(false)
    setInviteUrl(null)
    create.reset()
  }

  const close = (): void => {
    reset()
    onClose()
  }

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    const body: CreateInvitationRequest = {
      email: email.trim(),
      role: isAdmin ? 'admin' : 'member',
    }

    create.mutate(body, {
      onSuccess: (invitation) => {
        const link = readText(readField(invitation, 'invite_url'))

        if (link !== null) setInviteUrl(link)
        toast.success('An invitation has been created.')
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That account could not be created.'))
      },
    })
  }

  const fieldError = (field: string): string | null => {
    const error: unknown = create.error
    const details = readField(error, 'fieldErrors')
    const messages = readField(details, field)
    return Array.isArray(messages) ? readText(messages[0]) : null
  }

  if (inviteUrl !== null) {
    return (
      <Modal
        open={open}
        onClose={close}
        title="Invitation created"
        description="This link is shown once. It is not stored anywhere you can read it back."
        size="md"
        closeOnOverlayClick={false}
        footer={<Button variant="primary" onClick={close}>Done</Button>}
      >
        <div className={styles.secret}>
          <p className={styles.small}>
            The invitation has been emailed to {email.trim()}. If mail is not working on this
            server, send this link yourself over a channel you trust.
          </p>
          <div className={styles.secretValue}>
            <span className={styles.secretText}>{inviteUrl}</span>
            <Button onClick={() => copy(inviteUrl, 'The invitation link')}>Copy</Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add someone"
      description="Send an invitation. They choose their username, display name and password when they join."
      size="md"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" form="add-person-form" variant="primary" loading={create.isPending}>
            Send invitation
          </Button>
        </>
      }
    >
      <form id="add-person-form" className={styles.stack} onSubmit={submit} noValidate>
        <TextField
          label="Email address"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldError('email')}
          autoComplete="off"
        />
        <Checkbox
          label="Make this person an administrator"
          checked={isAdmin}
          onChange={(event) => setIsAdmin(event.target.checked)}
          hint="Administrators can manage the server after they accept the invitation."
        />
      </form>
    </Modal>
  )
}
